import { useState, useEffect, useCallback } from "react";
import {
  getAvailableCurrencies,
  getAvailableTimezones,
  extractErrorMessage,
  api,
  testGoogleMapsConnection,
  getGoogleMapsDiagnostics,
  getGoogleMapsStatus,
  updateGoogleMapsApiKey,
  revealGoogleMapsKey,
} from "../lib/api";
import { usePlatformSettings } from "../components/settings/PlatformSettingsContext";
import { useTheme } from "../components/theme/ThemeProvider";
import { LoadingSpinner } from "../components/shared/DataComponents";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { toast } from "sonner";
import {
  Settings,
  Globe,
  Palette,
  Eye,
  EyeOff,
  Sun,
  Moon,
  Monitor,
  Save,
  Check,
  Loader2,
  MapPin,
  Key,
  Zap,
  AlertTriangle,
  CheckCircle2,
  Info,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Activity,
  Shield,
  XCircle,
  Mail,
  Building,
} from "lucide-react";
import { cn } from "../lib/utils";

// Date format options
const DATE_FORMATS = [
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY", example: "22/01/2026" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY", example: "01/22/2026" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD", example: "2026-01-22" },
];

// Time format options
const TIME_FORMATS = [
  { value: "24-hour", label: "24-hour", example: "14:30" },
  { value: "12-hour", label: "12-hour (AM/PM)", example: "2:30 PM" },
];

// Number format options
const NUMBER_FORMATS = [
  { value: "1,234.56", label: "1,234.56 (UK/US)" },
  { value: "1.234,56", label: "1.234,56 (Europe)" },
  { value: "1 234,56", label: "1 234,56 (France)" },
];

export default function PlatformSettingsPage() {
  const { settings, loading: settingsLoading, updateSettings } = usePlatformSettings();
  const { theme, setTheme } = useTheme();
  
  const [currencies, setCurrencies] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // EF-TRAVEL-ADVISOR-01: Google Services state
  const [googleMapsEnabled, setGoogleMapsEnabled] = useState(false);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState("");
  const [savingGoogleKey, setSavingGoogleKey] = useState(false);
  
  // EF-TRAVEL-ADVISOR-02: Google Maps API Test state
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success, message, details }
  
  // EF-TRAVEL-ADVISOR-03: Google Setup Help modal state
  const [showHelpModal, setShowHelpModal] = useState(false);
  
  // EF-GOOGLE-KEY-HARDENING-01: Diagnostics and key reveal state
  const [diagnosticsExpanded, setDiagnosticsExpanded] = useState(false);
  const [diagnosticsData, setDiagnosticsData] = useState(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [keyMasked, setKeyMasked] = useState(null);
  const [revealedKey, setRevealedKey] = useState(null);
  const [revealingKey, setRevealingKey] = useState(false);
  const [revealCountdown, setRevealCountdown] = useState(0);
  
  const [formData, setFormData] = useState({
    date_format: "DD/MM/YYYY",
    time_format: "24-hour",
    timezone: "UTC",
    currency: "GBP",
    number_format: "1,234.56",
    theme_mode: "dark",
    primary_color: "",
    secondary_color: "",
    high_contrast_mode: false,
    // EF-BRANDING-SYSTEM-V2: Platform branding fields
    platform_name: "EventsFlo",
    platform_logo_url: "",
    platform_logo_dark_url: "",
    platform_favicon_url: "",
  });

  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [currenciesRes, timezonesRes] = await Promise.all([
          getAvailableCurrencies(),
          getAvailableTimezones(),
        ]);
        setCurrencies(currenciesRes.data.currencies || []);
        setTimezones(timezonesRes.data.timezones || []);
      } catch (err) {
        console.error("Failed to load reference data:", err);
      }
    };
    loadReferenceData();
  }, []);

  // Sync form with loaded settings
  useEffect(() => {
    if (!settingsLoading && settings) {
      setFormData({
        date_format: settings.date_format || "DD/MM/YYYY",
        time_format: settings.time_format || "24-hour",
        timezone: settings.timezone || "UTC",
        currency: settings.currency || "GBP",
        number_format: settings.number_format || "1,234.56",
        theme_mode: settings.theme_mode || theme,
        primary_color: settings.primary_color || "",
        secondary_color: settings.secondary_color || "",
        high_contrast_mode: settings.high_contrast_mode || false,
        // EF-BRANDING-SYSTEM-V2: Platform branding fields
        platform_name: settings.platform_name || "EventsFlo",
        platform_logo_url: settings.platform_logo_url || "",
        platform_logo_dark_url: settings.platform_logo_dark_url || "",
        platform_favicon_url: settings.platform_favicon_url || "",
      });
      // EF-TRAVEL-ADVISOR-01: Sync Google Maps settings
      setGoogleMapsEnabled(settings.google_maps_api_enabled || false);
    }
  }, [settings, settingsLoading, theme]);

  // EF-GOOGLE-KEY-HARDENING-01: Load initial key status
  useEffect(() => {
    const loadKeyStatus = async () => {
      try {
        const response = await getGoogleMapsStatus();
        setKeyMasked(response.data.api_key_masked || null);
      } catch (err) {
        console.error("Failed to load key status:", err);
      }
    };
    loadKeyStatus();
  }, []);

  // EF-GOOGLE-KEY-HARDENING-01: Auto-hide revealed key after countdown
  useEffect(() => {
    if (revealCountdown > 0) {
      const timer = setTimeout(() => {
        setRevealCountdown(revealCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (revealCountdown === 0 && revealedKey) {
      setRevealedKey(null);
    }
  }, [revealCountdown, revealedKey]);

  // EF-GOOGLE-KEY-HARDENING-01: Load diagnostics when expanded
  const loadDiagnostics = useCallback(async () => {
    setLoadingDiagnostics(true);
    try {
      const response = await getGoogleMapsDiagnostics();
      setDiagnosticsData(response.data);
    } catch (err) {
      toast.error("Failed to load diagnostics");
    } finally {
      setLoadingDiagnostics(false);
    }
  }, []);

  // EF-GOOGLE-KEY-HARDENING-01: Reveal API key
  const handleRevealKey = async () => {
    setRevealingKey(true);
    try {
      const response = await revealGoogleMapsKey();
      if (response.data.success) {
        setRevealedKey(response.data.api_key);
        setRevealCountdown(response.data.expires_in_seconds);
        toast.success("API key revealed - will auto-hide in 30 seconds");
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to reveal key");
    } finally {
      setRevealingKey(false);
    }
  };

  // EF-GOOGLE-KEY-HARDENING-01: Update API key with new endpoint
  const handleSaveApiKey = async () => {
    if (!googleMapsApiKey.trim()) {
      toast.error("Please enter an API key");
      return;
    }
    setSavingGoogleKey(true);
    setTestResult(null);
    try {
      const response = await updateGoogleMapsApiKey(googleMapsApiKey);
      if (response.data.success) {
        toast.success("Google Maps API key saved and encrypted");
        setKeyMasked(response.data.key_masked);
        setGoogleMapsApiKey(""); // Clear for security
        // Refresh diagnostics if expanded
        if (diagnosticsExpanded) {
          loadDiagnostics();
        }
      } else {
        toast.error(response.data.message || "Failed to save API key");
      }
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to save API key");
    } finally {
      setSavingGoogleKey(false);
    }
  };

  // EF-GMAIL-SMTP-01: Load email settings
  // Handle form changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Apply theme change immediately
    if (field === "theme_mode" && value !== "system") {
      setTheme(value);
    }
  };

  // Save settings
  const handleSave = async () => {
    setSaving(true);
    try {
      // Prepare data - convert empty strings to null for optional fields
      const saveData = {
        ...formData,
        primary_color: formData.primary_color || null,
        secondary_color: formData.secondary_color || null,
      };
      
      await updateSettings(saveData);
      
      // Update theme
      if (formData.theme_mode !== "system") {
        setTheme(formData.theme_mode);
      }
      
      toast.success("Platform settings saved successfully");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // Validate hex color
  const isValidHexColor = (color) => {
    if (!color) return true; // Empty is allowed
    return /^#[0-9A-Fa-f]{6}$/.test(color);
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header - EF-SETTINGS-CONSOLIDATION-V1: Added breadcrumb */}
      <div className="flex items-center justify-between">
        <div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <a href="/settings" className="hover:text-foreground">Settings</a>
            <span>/</span>
            <span className="text-foreground">Platform</span>
          </div>
          <h1 className="text-2xl font-bold font-secondary tracking-tight flex items-center gap-2">
            <Settings className="h-6 w-6 text-primary" />
            Platform Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure localisation, appearance, and accessibility options
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-primary hover:bg-primary/90"
          data-testid="save-platform-settings-btn"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>

      {/* Localisation Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="h-5 w-5 text-primary" />
            Localisation
          </CardTitle>
          <CardDescription>
            Configure date, time, currency, and number formats for your region
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date Format */}
            <div className="space-y-2">
              <Label htmlFor="date_format">Date Format</Label>
              <Select
                value={formData.date_format}
                onValueChange={(value) => handleChange("date_format", value)}
              >
                <SelectTrigger data-testid="date-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_FORMATS.map((fmt) => (
                    <SelectItem key={fmt.value} value={fmt.value}>
                      <span className="flex items-center justify-between w-full gap-4">
                        <span>{fmt.label}</span>
                        <span className="text-muted-foreground text-xs">e.g., {fmt.example}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time Format */}
            <div className="space-y-2">
              <Label htmlFor="time_format">Time Format</Label>
              <Select
                value={formData.time_format}
                onValueChange={(value) => handleChange("time_format", value)}
              >
                <SelectTrigger data-testid="time-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_FORMATS.map((fmt) => (
                    <SelectItem key={fmt.value} value={fmt.value}>
                      <span className="flex items-center justify-between w-full gap-4">
                        <span>{fmt.label}</span>
                        <span className="text-muted-foreground text-xs">e.g., {fmt.example}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Timezone */}
            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select
                value={formData.timezone}
                onValueChange={(value) => handleChange("timezone", value)}
              >
                <SelectTrigger data-testid="timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Currency */}
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => handleChange("currency", value)}
              >
                <SelectTrigger data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((curr) => (
                    <SelectItem key={curr.code} value={curr.code}>
                      <span className="flex items-center gap-2">
                        <span className="font-mono">{curr.symbol}</span>
                        <span>{curr.name} ({curr.code})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Number Format */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="number_format">Number Format</Label>
              <Select
                value={formData.number_format}
                onValueChange={(value) => handleChange("number_format", value)}
              >
                <SelectTrigger data-testid="number-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NUMBER_FORMATS.map((fmt) => (
                    <SelectItem key={fmt.value} value={fmt.value}>
                      {fmt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Theme & Appearance Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Palette className="h-5 w-5 text-primary" />
            Theme & Appearance
          </CardTitle>
          <CardDescription>
            Customize the visual appearance of the platform
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Theme Mode */}
          <div className="space-y-3">
            <Label>Theme Mode</Label>
            <div className="flex gap-3">
              {[
                { value: "light", label: "Light", icon: Sun },
                { value: "dark", label: "Dark", icon: Moon },
                { value: "system", label: "System", icon: Monitor },
              ].map((opt) => (
                <Button
                  key={opt.value}
                  variant={formData.theme_mode === opt.value ? "default" : "outline"}
                  className={cn(
                    "flex-1 gap-2",
                    formData.theme_mode === opt.value && "bg-primary text-primary-foreground"
                  )}
                  onClick={() => handleChange("theme_mode", opt.value)}
                  data-testid={`theme-${opt.value}-btn`}
                >
                  <opt.icon className="h-4 w-4" />
                  {opt.label}
                  {formData.theme_mode === opt.value && (
                    <Check className="h-4 w-4 ml-auto" />
                  )}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              System mode will follow your device&apos;s theme preference
            </p>
          </div>

          <Separator />

          {/* Brand Colors */}
          <div className="space-y-4">
            <Label>Brand Colors</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Primary Color */}
              <div className="space-y-2">
                <Label htmlFor="primary_color" className="text-sm text-muted-foreground">
                  Primary Brand Colour
                </Label>
                <div className="flex gap-2">
                  <div
                    className="w-10 h-10 rounded border-2 border-border shrink-0"
                    style={{ backgroundColor: formData.primary_color || "#00B4D8" }}
                  />
                  <Input
                    id="primary_color"
                    type="text"
                    placeholder="#00B4D8"
                    value={formData.primary_color}
                    onChange={(e) => handleChange("primary_color", e.target.value)}
                    className={cn(
                      !isValidHexColor(formData.primary_color) && "border-red-500"
                    )}
                    data-testid="primary-color-input"
                  />
                </div>
                {!isValidHexColor(formData.primary_color) && (
                  <p className="text-xs text-red-500">Invalid hex color (use format #RRGGBB)</p>
                )}
              </div>

              {/* Secondary Color */}
              <div className="space-y-2">
                <Label htmlFor="secondary_color" className="text-sm text-muted-foreground">
                  Secondary Accent Colour (optional)
                </Label>
                <div className="flex gap-2">
                  <div
                    className="w-10 h-10 rounded border-2 border-border shrink-0"
                    style={{ backgroundColor: formData.secondary_color || "#6B7280" }}
                  />
                  <Input
                    id="secondary_color"
                    type="text"
                    placeholder="#6B7280"
                    value={formData.secondary_color}
                    onChange={(e) => handleChange("secondary_color", e.target.value)}
                    className={cn(
                      !isValidHexColor(formData.secondary_color) && "border-red-500"
                    )}
                    data-testid="secondary-color-input"
                  />
                </div>
                {!isValidHexColor(formData.secondary_color) && (
                  <p className="text-xs text-red-500">Invalid hex color (use format #RRGGBB)</p>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Brand colours affect UI accents only. Document branding is configured separately.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* EF-BRANDING-SYSTEM-V2: Platform Branding Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building className="h-5 w-5 text-primary" />
            Platform Branding
          </CardTitle>
          <CardDescription>
            Configure EventsFlo platform branding (used for platform-level pages)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Platform Name */}
          <div className="space-y-2">
            <Label htmlFor="platform_name">Platform Name</Label>
            <Input
              id="platform_name"
              data-testid="platform-name-input"
              value={formData.platform_name}
              onChange={(e) => handleChange("platform_name", e.target.value)}
              placeholder="e.g., EventsFlo"
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">
              The platform name shown on login pages and PWA manifest when no tenant context
            </p>
          </div>

          {/* Platform Logo URL */}
          <div className="space-y-2">
            <Label htmlFor="platform_logo_url">Platform Logo URL</Label>
            <div className="flex gap-4 items-start">
              <div className="flex-1 space-y-2">
                <Input
                  id="platform_logo_url"
                  data-testid="platform-logo-url-input"
                  value={formData.platform_logo_url}
                  onChange={(e) => handleChange("platform_logo_url", e.target.value)}
                  placeholder="https://example.com/logo.png"
                  type="url"
                />
                <p className="text-xs text-muted-foreground">
                  URL to the platform logo (transparent PNG recommended, 512x512px)
                </p>
              </div>
              {formData.platform_logo_url && (
                <div className="w-16 h-16 border rounded-lg flex items-center justify-center bg-muted/50 shrink-0">
                  <img
                    src={formData.platform_logo_url}
                    alt="Platform logo preview"
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<span class="text-xs text-muted-foreground">Invalid URL</span>';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Platform Logo Dark URL (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="platform_logo_dark_url">Platform Logo (Dark Mode) - Optional</Label>
            <div className="flex gap-4 items-start">
              <div className="flex-1 space-y-2">
                <Input
                  id="platform_logo_dark_url"
                  data-testid="platform-logo-dark-url-input"
                  value={formData.platform_logo_dark_url}
                  onChange={(e) => handleChange("platform_logo_dark_url", e.target.value)}
                  placeholder="https://example.com/logo-dark.png"
                  type="url"
                />
                <p className="text-xs text-muted-foreground">
                  Optional alternative logo for dark mode backgrounds
                </p>
              </div>
              {formData.platform_logo_dark_url && (
                <div className="w-16 h-16 border rounded-lg flex items-center justify-center bg-gray-900 shrink-0">
                  <img
                    src={formData.platform_logo_dark_url}
                    alt="Platform logo dark preview"
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<span class="text-xs text-gray-400">Invalid URL</span>';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Platform Favicon URL (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="platform_favicon_url">Platform Favicon URL - Optional</Label>
            <div className="flex gap-4 items-start">
              <div className="flex-1 space-y-2">
                <Input
                  id="platform_favicon_url"
                  data-testid="platform-favicon-url-input"
                  value={formData.platform_favicon_url}
                  onChange={(e) => handleChange("platform_favicon_url", e.target.value)}
                  placeholder="https://example.com/favicon.ico"
                  type="url"
                />
                <p className="text-xs text-muted-foreground">
                  Browser tab icon (ICO or PNG, 32x32px or 64x64px)
                </p>
              </div>
              {formData.platform_favicon_url && (
                <div className="w-10 h-10 border rounded flex items-center justify-center bg-muted/50 shrink-0">
                  <img
                    src={formData.platform_favicon_url}
                    alt="Favicon preview"
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accessibility Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Eye className="h-5 w-5 text-primary" />
            Accessibility
          </CardTitle>
          <CardDescription>
            Improve readability and accessibility across the platform
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* High Contrast Mode */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="high_contrast_mode">High Contrast Mode</Label>
              <p className="text-sm text-muted-foreground">
                Increase text contrast for better readability (WCAG AA compliant)
              </p>
            </div>
            <Switch
              id="high_contrast_mode"
              checked={formData.high_contrast_mode}
              onCheckedChange={(checked) => handleChange("high_contrast_mode", checked)}
              data-testid="high-contrast-toggle"
            />
          </div>

          <Separator />

          {/* Contrast Preview */}
          <div className="space-y-3">
            <Label>Contrast Preview</Label>
            <div className="grid grid-cols-2 gap-4">
              {/* Light Mode Preview */}
              <div className="bg-white border rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-gray-900">Light Mode</p>
                <p className="text-sm text-gray-700">Primary text sample</p>
                <p className="text-sm text-gray-600">Secondary text sample</p>
                <p className="text-xs text-gray-500">Muted text sample</p>
                <div className="text-xs text-gray-400">Disabled state</div>
              </div>
              
              {/* Dark Mode Preview */}
              <div className="bg-surface-base border border-border rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-white">Dark Mode</p>
                <p className="text-sm text-foreground">Primary text sample</p>
                <p className="text-sm text-foreground">Secondary text sample</p>
                <p className="text-xs text-muted-foreground">Muted text sample</p>
                <div className="text-xs text-muted-foreground">Disabled state</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* EF-TRAVEL-ADVISOR-01: Google Services Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="h-5 w-5 text-primary" />
                Google Services
              </CardTitle>
              <CardDescription>
                Configure Google Maps integration for travel estimation
              </CardDescription>
            </div>
            {/* EF-TRAVEL-ADVISOR-03: Help Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHelpModal(true)}
              data-testid="google-setup-help-btn"
            >
              <Info className="h-4 w-4 mr-2" />
              Google Setup Help
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* EF-TRAVEL-ADVISOR-03: Configuration Warning Banner */}
          {(!googleMapsEnabled || (testResult && !testResult.success && testResult.errorType === "API_DISABLED")) && (
            <div
              className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4"
              data-testid="google-maps-config-warning"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-500">Configuration Required</p>
                  <p className="text-sm text-foreground mt-1">
                    Google Maps API is not fully configured. Travel Advisor will operate in advisory-only test mode.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Google Maps API Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="google_maps_enabled">Enable Google Maps API</Label>
              <p className="text-sm text-muted-foreground">
                Enables automatic travel distance and duration calculations
              </p>
            </div>
            <Switch
              id="google_maps_enabled"
              checked={googleMapsEnabled}
              onCheckedChange={async (checked) => {
                setGoogleMapsEnabled(checked);
                setTestResult(null); // Clear previous test result
                try {
                  await updateSettings({ google_maps_api_enabled: checked });
                  toast.success(checked ? "Google Maps API enabled" : "Google Maps API disabled");
                } catch (err) {
                  toast.error("Failed to update setting");
                  setGoogleMapsEnabled(!checked);
                }
              }}
              data-testid="google-maps-toggle"
            />
          </div>

          <Separator />

          {/* Google Maps API Key - EF-GOOGLE-KEY-HARDENING-01 */}
          <div className="space-y-3">
            <Label htmlFor="google_maps_api_key" className="flex items-center gap-2">
              <Key className="h-4 w-4 text-muted-foreground" />
              Google Maps API Key
            </Label>
            
            {/* Current Key Status */}
            {keyMasked && (
              <div className="flex items-center gap-2 p-3 bg-surface-elevated rounded-lg border">
                <Shield className="h-4 w-4 text-emerald-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    {revealedKey ? (
                      <span className="font-mono">{revealedKey}</span>
                    ) : (
                      <span className="font-mono">{keyMasked}</span>
                    )}
                  </p>
                  {revealCountdown > 0 && (
                    <p className="text-xs text-amber-500 mt-1">
                      Auto-hiding in {revealCountdown}s
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={revealedKey ? () => { setRevealedKey(null); setRevealCountdown(0); } : handleRevealKey}
                  disabled={revealingKey}
                  data-testid="reveal-api-key-btn"
                >
                  {revealingKey ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : revealedKey ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
            
            {/* New Key Input */}
            <div className="flex gap-2">
              <Input
                id="google_maps_api_key"
                type="password"
                placeholder={keyMasked ? "Enter new API key to replace existing" : "Enter your Google Maps API key"}
                value={googleMapsApiKey}
                onChange={(e) => setGoogleMapsApiKey(e.target.value)}
                className="flex-1"
                data-testid="google-maps-api-key-input"
              />
              <Button
                variant="outline"
                onClick={handleSaveApiKey}
                disabled={savingGoogleKey}
                data-testid="save-google-api-key-btn"
              >
                {savingGoogleKey ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Get your API key from the{" "}
              <a 
                href="https://console.cloud.google.com/apis/credentials" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Google Cloud Console
              </a>
              . Requires Distance Matrix API enabled.
            </p>
          </div>

          <Separator />

          {/* EF-TRAVEL-ADVISOR-02: Test Connection */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-muted-foreground" />
                  Test Connection
                </Label>
                <p className="text-sm text-muted-foreground">
                  Verify your Google Maps API key is working correctly
                </p>
              </div>
              <Button
                variant="outline"
                onClick={async () => {
                  setTestingConnection(true);
                  setTestResult(null);
                  try {
                    const response = await testGoogleMapsConnection();
                    const data = response.data;
                    
                    if (data.success) {
                      setTestResult({
                        success: true,
                        message: data.message,
                        details: {
                          distance_km: data.distance_km,
                          duration_minutes: data.duration_minutes,
                          raw_status: data.raw_status
                        }
                      });
                    } else {
                      setTestResult({
                        success: false,
                        message: data.message,
                        errorType: data.error_type
                      });
                    }
                  } catch (err) {
                    setTestResult({
                      success: false,
                      message: extractErrorMessage(err) || "Connection test failed",
                      errorType: "NETWORK_ERROR"
                    });
                  } finally {
                    setTestingConnection(false);
                  }
                }}
                disabled={testingConnection}
                data-testid="test-google-maps-btn"
              >
                {testingConnection ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Test Google Maps Connection
                  </>
                )}
              </Button>
            </div>

            {/* Test Result Panel */}
            {testResult && (
              <div
                className={cn(
                  "rounded-lg border p-4",
                  testResult.success
                    ? "bg-emerald-500/10 border-emerald-500/30"
                    : "bg-red-500/10 border-red-500/30"
                )}
                data-testid="google-maps-test-result"
              >
                <div className="flex items-start gap-3">
                  {testResult.success ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  )}
                  <div className="space-y-2 flex-1">
                    <p className={cn(
                      "font-medium",
                      testResult.success ? "text-emerald-500" : "text-red-500"
                    )}>
                      {testResult.success ? "Connection Successful" : "Connection Failed"}
                    </p>
                    <p className="text-sm text-foreground">
                      {testResult.message}
                    </p>
                    {testResult.success && testResult.details && (
                      <div className="mt-3 pt-3 border-t border-emerald-500/20">
                        <p className="text-xs text-muted-foreground mb-2">Test Route Results:</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Distance:</span>{" "}
                            <span className="font-medium">{testResult.details.distance_km} km</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Duration:</span>{" "}
                            <span className="font-medium">{testResult.details.duration_minutes} min</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {!testResult.success && testResult.errorType && (
                      <p className="text-xs text-red-400 mt-2">
                        Error Code: {testResult.errorType}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* EF-GOOGLE-KEY-HARDENING-01: Connection Diagnostics */}
          <Collapsible
            open={diagnosticsExpanded}
            onOpenChange={(open) => {
              setDiagnosticsExpanded(open);
              if (open && !diagnosticsData) {
                loadDiagnostics();
              }
            }}
          >
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between p-0 h-auto hover:bg-transparent"
                data-testid="diagnostics-toggle"
              >
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Connection Diagnostics</span>
                </div>
                {diagnosticsExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              {loadingDiagnostics ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : diagnosticsData ? (
                <div className="space-y-3">
                  {/* Diagnostics Table */}
                  <div className="rounded-lg border bg-surface-elevated overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b">
                          <td className="px-3 py-2 text-muted-foreground">API Enabled</td>
                          <td className="px-3 py-2 text-right">
                            {diagnosticsData.google_maps_enabled ? (
                              <span className="flex items-center justify-end gap-1 text-emerald-500">
                                <CheckCircle2 className="h-4 w-4" /> Yes
                              </span>
                            ) : (
                              <span className="flex items-center justify-end gap-1 text-red-500">
                                <XCircle className="h-4 w-4" /> No
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="px-3 py-2 text-muted-foreground">API Key Present</td>
                          <td className="px-3 py-2 text-right">
                            {diagnosticsData.api_key_present ? (
                              <span className="flex items-center justify-end gap-1 text-emerald-500">
                                <CheckCircle2 className="h-4 w-4" /> Yes
                              </span>
                            ) : (
                              <span className="flex items-center justify-end gap-1 text-red-500">
                                <XCircle className="h-4 w-4" /> No
                              </span>
                            )}
                          </td>
                        </tr>
                        {diagnosticsData.api_key_length && (
                          <tr className="border-b">
                            <td className="px-3 py-2 text-muted-foreground">Key Length</td>
                            <td className="px-3 py-2 text-right font-mono">{diagnosticsData.api_key_length} chars</td>
                          </tr>
                        )}
                        {diagnosticsData.api_key_masked && (
                          <tr className="border-b">
                            <td className="px-3 py-2 text-muted-foreground">Key (Masked)</td>
                            <td className="px-3 py-2 text-right font-mono">{diagnosticsData.api_key_masked}</td>
                          </tr>
                        )}
                        <tr className="border-b">
                          <td className="px-3 py-2 text-muted-foreground">Billing Linked</td>
                          <td className="px-3 py-2 text-right">
                            {diagnosticsData.billing_linked === null ? (
                              <span className="text-muted-foreground">Unknown</span>
                            ) : diagnosticsData.billing_linked ? (
                              <span className="flex items-center justify-end gap-1 text-emerald-500">
                                <CheckCircle2 className="h-4 w-4" /> Yes
                              </span>
                            ) : (
                              <span className="flex items-center justify-end gap-1 text-red-500">
                                <XCircle className="h-4 w-4" /> No
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="px-3 py-2 text-muted-foreground">Distance Matrix API</td>
                          <td className="px-3 py-2 text-right">
                            {diagnosticsData.distance_matrix_enabled === null ? (
                              <span className="text-muted-foreground">Unknown</span>
                            ) : diagnosticsData.distance_matrix_enabled ? (
                              <span className="flex items-center justify-end gap-1 text-emerald-500">
                                <CheckCircle2 className="h-4 w-4" /> Enabled
                              </span>
                            ) : (
                              <span className="flex items-center justify-end gap-1 text-red-500">
                                <XCircle className="h-4 w-4" /> Not Enabled
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="px-3 py-2 text-muted-foreground">Last Test Status</td>
                          <td className="px-3 py-2 text-right">
                            {diagnosticsData.last_test_status === "OK" ? (
                              <span className="flex items-center justify-end gap-1 text-emerald-500">
                                <CheckCircle2 className="h-4 w-4" /> OK
                              </span>
                            ) : diagnosticsData.last_test_status ? (
                              <span className="flex items-center justify-end gap-1 text-red-500">
                                <XCircle className="h-4 w-4" /> {diagnosticsData.last_test_status}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Not Tested</span>
                            )}
                          </td>
                        </tr>
                        {diagnosticsData.last_test_error && (
                          <tr className="border-b">
                            <td className="px-3 py-2 text-muted-foreground">Last Error</td>
                            <td className="px-3 py-2 text-right text-red-400 text-xs">{diagnosticsData.last_test_error}</td>
                          </tr>
                        )}
                        {diagnosticsData.last_test_timestamp && (
                          <tr>
                            <td className="px-3 py-2 text-muted-foreground">Last Tested</td>
                            <td className="px-3 py-2 text-right text-xs">
                              {new Date(diagnosticsData.last_test_timestamp).toLocaleString()}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Refresh Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadDiagnostics}
                    disabled={loadingDiagnostics}
                  >
                    <Activity className="h-4 w-4 mr-2" />
                    Refresh Diagnostics
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Click to load diagnostics...</p>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Email Services - Redirect to Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5 text-primary" />
            Email Services
          </CardTitle>
          <CardDescription>
            Configure email sending for quotes, invoices, and notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-surface-elevated rounded-lg border">
            <div className="space-y-1">
              <p className="font-medium">Email Provider Configuration</p>
              <p className="text-sm text-muted-foreground">
                Manage email providers, test connections, and view send logs
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/settings/email'}
              data-testid="go-to-email-settings-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              Open Email Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save Button (bottom) */}
      <div className="flex justify-end pt-4">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          size="lg"
          className="bg-primary hover:bg-primary/90"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save All Settings
        </Button>
      </div>

      {/* EF-TRAVEL-ADVISOR-03: Google Setup Help Modal */}
      <Dialog open={showHelpModal} onOpenChange={setShowHelpModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="google-setup-help-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Google Maps API Setup Guide
            </DialogTitle>
            <DialogDescription>
              Follow these steps to configure Google Maps for travel estimation
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Section 1: Required APIs */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold">1</span>
                Required APIs
              </h3>
              <ul className="ml-8 space-y-2 text-sm text-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span><strong>Distance Matrix API</strong> — required for travel calculations</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span><strong>Geocoding API</strong> — recommended for address validation</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                  <span><strong>Maps JavaScript API</strong> — optional, for future map displays</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                  <span><strong>Routes API</strong> — optional, for advanced routing</span>
                </li>
              </ul>
            </div>

            <Separator />

            {/* Section 2: Setup Steps */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold">2</span>
                Setup Steps
              </h3>
              <ol className="ml-8 space-y-2 text-sm text-foreground list-decimal list-inside">
                <li>Go to the Google Cloud Console</li>
                <li>Select an existing project or create a new one</li>
                <li>Navigate to <strong>APIs &amp; Services → Library</strong></li>
                <li>Search for and enable the required APIs listed above</li>
                <li>Go to <strong>APIs &amp; Services → Credentials</strong></li>
                <li>Click <strong>Create Credentials → API Key</strong></li>
                <li>Restrict the API key (HTTP referrer or server IP recommended)</li>
                <li>Copy the API key and paste it into EventsFlo settings</li>
                <li>Click <strong>&quot;Test Google Maps Connection&quot;</strong> to verify</li>
              </ol>
            </div>

            <Separator />

            {/* Section 3: Official Links */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold">3</span>
                Official Documentation
              </h3>
              <div className="ml-8 space-y-2">
                <a
                  href="https://console.cloud.google.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                  data-testid="help-link-console"
                >
                  <ExternalLink className="h-4 w-4" />
                  Google Cloud Console
                </a>
                <a
                  href="https://developers.google.com/maps/documentation/distance-matrix"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                  data-testid="help-link-distance-matrix"
                >
                  <ExternalLink className="h-4 w-4" />
                  Distance Matrix API Documentation
                </a>
                <a
                  href="https://developers.google.com/maps/get-started"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                  data-testid="help-link-get-started"
                >
                  <ExternalLink className="h-4 w-4" />
                  Google Maps Platform - Get Started
                </a>
              </div>
            </div>

            <Separator />

            {/* Section 4: Disclaimer */}
            <div className="space-y-2 bg-surface-elevated rounded-lg p-4">
              <p className="text-xs text-muted-foreground" data-testid="help-disclaimer">
                <strong>Disclaimer:</strong> External links and setup steps are valid at the time of implementation. 
                Google may update documentation, pricing, or setup requirements. Please refer to official 
                Google documentation if steps differ.
              </p>
            </div>
          </div>

          {/* Close Button */}
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setShowHelpModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
